export type BilingualText = {
  ar: string;
  en: string;
};

export type VehicleTestPoint = BilingualText;

export type VehicleTestGroup = {
  title: BilingualText;
  items: readonly VehicleTestPoint[];
};

function point(ar: string, en: string): VehicleTestPoint {
  return { ar, en };
}

export const VEHICLE_TEST_POINTS_HEADING: BilingualText = {
  ar: "نقاط الفحص",
  en: "Inspection points",
};

export const VEHICLE_TEST_POINTS_TITLE = `${VEHICLE_TEST_POINTS_HEADING.ar} · ${VEHICLE_TEST_POINTS_HEADING.en}`;

export type VehicleTestSection = {
  id: string;
  title: BilingualText;
  intro: string;
  groups: readonly VehicleTestGroup[];
};

export const VEHICLE_TEST_SECTIONS: readonly VehicleTestSection[] = [
  {
    id: "equipment",
    title: { ar: "التقنية والتجهيزات", en: "Technology and equipment" },
    intro: "نراجع ما يركبه المصنع في السيارة: الهيكل الحامل، الدفع، التعليق، التوجيه، الإنارة، والكهرباء. التجهيز الإضافي يُذكر إن كان موجوداً.",
    groups: [
      {
        title: { ar: "الهيكل الحامل", en: "Structural frame" },
        items: [
          point("العتبة الجانبية", "Side sill"),
          point("العارضة الأمامية", "Front crossmember"),
          point("العارضة الخلفية", "Rear crossmember"),
          point("أرضية المقصورة", "Cabin floor"),
          point("بيت العجلة الأمامي", "Front wheel arch"),
          point("بيت العجلة الخلفي", "Rear wheel arch"),
          point("عمود السقف", "Roof pillar"),
          point("عارضة السقف", "Roof rail"),
        ],
      },
      {
        title: { ar: "منظومة الدفع", en: "Drivetrain" },
        items: [
          point("المحرك من ناحية التسريب الظاهر", "Engine for visible leaks"),
          point("منظومة الوقود", "Fuel system"),
          point("منظومة العادم", "Exhaust system"),
          point("نقل الحركة", "Transmission"),
          point("وصلة الدفع", "Driveshaft joint"),
          point("تغذية الكهرباء", "Electrical supply"),
          point("بطارية التشغيل", "Starter battery"),
        ],
      },
      {
        title: { ar: "العجلات والتعليق", en: "Wheels and suspension" },
        items: [
          point("الإطارات", "Tyres"),
          point("صامولة القفل أو قفل الجنط", "Locking wheel nut"),
          point("ممتص الصدمات", "Shock absorber"),
          point("محامل العجلات", "Wheel bearings"),
          point("مفصل المحور", "Hub joint"),
          point("ذراع التحكم الأمامي", "Front control arm"),
          point("ذراع التحكم الخلفي", "Rear control arm"),
          point("النابض", "Spring"),
          point("قاعدة النابض", "Spring seat"),
        ],
      },
      {
        title: { ar: "التوجيه", en: "Steering" },
        items: [
          point("مفصل التوجيه", "Steering joint"),
          point("علبة التوجيه", "Steering rack"),
          point("ذراع التوجيه", "Steering arm"),
          point("مؤازر التوجيه", "Power steering"),
        ],
      },
      {
        title: { ar: "الإنارة والإشارات", en: "Lights and signals" },
        items: [
          point("مساحات الزجاج", "Wipers"),
          point("غاسل الزجاج", "Washer"),
          point("المرآة الخارجية", "Exterior mirror"),
          point("المصابيح الأمامية", "Headlights"),
          point("منظف المصابيح", "Headlight washer"),
          point("البوق", "Horn"),
          point("أضواء التحديد الجانبية", "Side marker lights"),
          point("ضوء الموضع الأمامي", "Front position light"),
          point("ضوء الموضع الخلفي", "Rear position light"),
          point("إشارات الاتجاه", "Indicators"),
          point("ضوء لوحة الأرقام", "Number-plate light"),
          point("ضوء التوقف", "Brake light"),
          point("العاكس", "Reflector"),
        ],
      },
      {
        title: { ar: "العدادات", en: "Instruments" },
        items: [
          point("عداد السرعة", "Speedometer"),
          point("عداد المسافة", "Odometer"),
          point("مؤشرات التحذير", "Warning lights"),
        ],
      },
      {
        title: { ar: "الكهرباء والراحة", en: "Electrics and comfort" },
        items: [
          point("البطارية", "Battery"),
          point("المولد", "Alternator"),
          point("بادئ الحركة", "Starter motor"),
          point("لوحة العدادات", "Instrument panel"),
          point("الأزرار", "Switches"),
          point("أدوات التحكم", "Controls"),
          point("رسائل الأعطال", "Fault messages"),
          point("نظام الحماية", "Immobiliser"),
          point("حاسوب الرحلة", "Trip computer"),
          point("إضاءة المقصورة", "Interior lighting"),
          point("التدفئة والتهوية", "Heating and ventilation"),
          point("التكييف", "Air conditioning"),
          point("رافعات النوافذ", "Window regulators"),
          point("فتحة السقف", "Sunroof"),
          point("القفل المركزي", "Central locking"),
          point("تدفئة المقاعد", "Seat heating"),
          point("ضبط المقاعد", "Seat adjustment"),
          point("المرايا الكهربائية", "Electric mirrors"),
          point("النظام الصوتي", "Audio system"),
          point("منافذ الشحن", "Charging ports"),
        ],
      },
      {
        title: { ar: "السلامة المرافقة", en: "Safety kit" },
        items: [
          point("مثلث التحذير", "Warning triangle"),
          point("سترة عاكسة", "Reflective vest"),
          point("مثبتات مقاعد الأطفال", "Child-seat anchors"),
        ],
      },
      {
        title: { ar: "وثائق الخدمة", en: "Service documents" },
        items: [
          point("دليل المالك", "Owners manual"),
          point("سجل الصيانة", "Service history"),
          point("قراءة العداد عند الفحص", "Odometer at inspection"),
          point("ملاحظات الخدمة الأخرى", "Other service notes"),
        ],
      },
      {
        title: { ar: "تجهيز يُذكر إن وُجد", en: "Equipment noted if fitted" },
        items: [
          point("زجاج خصوصية", "Privacy glass"),
          point("مثبتات أيزوفيكس", "ISOFIX mounts"),
          point("مانع الانزلاق", "Stability control"),
          point("التحكم بالسحب", "Traction control"),
          point("نظام المساعدة على القيادة", "Driver assistance"),
          point("مدخل صوت إضافي", "Auxiliary input"),
          point("كاميرا الرجوع", "Reversing camera"),
          point("بلوتوث", "Bluetooth"),
          point("مدفأة وقود بمؤقت", "Fuel heater with timer"),
          point("خطاف السحب", "Towbar"),
          point("شاشات إضافية", "Extra screens"),
          point("تنبيه النقطة العمياء", "Blind-spot warning"),
          point("زجاج أمامي مدفأ", "Heated windscreen"),
          point("مثبت السرعة", "Cruise control"),
          point("تنبيه مغادرة المسار", "Lane-departure warning"),
          point("الملاحة", "Navigation"),
          point("عرض على الزجاج الأمامي", "Head-up display"),
          point("أضواء الانعطاف", "Cornering lights"),
          point("كابل الشحن", "Charging cable"),
          point("قضبان التحميل", "Roof rails"),
          point("واقي أرضية التحميل", "Load-floor liner"),
          point("تدليك مقعد السائق", "Driver seat massage"),
          point("شاشة الوسائط", "Media screen"),
          point("مدفأة المحرك", "Engine heater"),
          point("تشغيل دون مفتاح", "Keyless start"),
          point("سقف بانورامي", "Panoramic roof"),
          point("مساعد الركن", "Parking assist"),
          point("حساسات الركن", "Parking sensors"),
          point("مقود مدفأ", "Heated steering wheel"),
          point("حساس المطر", "Rain sensor"),
          point("تشغيل وإيقاف تلقائي", "Auto start-stop"),
          point("صندوق سقف", "Roof box"),
          point("وسادة هواء قابلة للتعطيل", "Switchable airbag"),
          point("منفذ يو إس بي", "USB port"),
          point("مقاعد مهواة", "Ventilated seats"),
          point("تدليك المقاعد", "Seat massage"),
        ],
      },
    ],
  },
  {
    id: "engine-gearbox",
    title: { ar: "المحرك وعلبة السرعات", en: "Engine and gearbox" },
    intro: "نجرّب السيارة على الطريق ونتابع صوت المحرك واستجابة علبة السرعات. نفحص أيضاً التبريد والتسريب وأعمدة الدفع وقراءة العداد.",
    groups: [
      {
        title: { ar: "المحرك وخط الدفع", en: "Engine and driveline" },
        items: [
          point("استجابة المحرك أثناء التجربة", "Engine response on the test drive"),
          point("علبة السرعات", "Gearbox"),
          point("منظومة التبريد", "Cooling system"),
          point("تسريب سوائل ظاهر", "Visible fluid leak"),
          point("وصلات الخراطيم", "Hose connections"),
          point("سيور القيادة الظاهرة", "Visible drive belts"),
          point("ضجيج أو خلل أثناء التشغيل", "Noise or fault while running"),
          point("العادم تحت الحمل", "Exhaust under load"),
          point("الدبرياج", "Clutch"),
          point("أعمدة الدفع", "Drive shafts"),
          point("خط الدفع", "Driveline"),
          point("تطابق قراءة العداد مع السجل", "Odometer matches the record"),
          point("بادئ الحركة عند التجربة", "Starter on the test drive"),
        ],
      },
    ],
  },
  {
    id: "brakes",
    title: { ar: "الفرامل", en: "Brakes" },
    intro: "الفرامل أهم تجهيز للسلامة، لذلك لا نكتفي بأنها تعمل الآن. ننظر إلى التآكل الذي قد يظهر قريباً، ونجري كبحاً أثناء تجربة الطريق.",
    groups: [
      {
        title: { ar: "منظومة الفرامل", en: "Brake system" },
        items: [
          point("فرامل الخدمة الأمامية", "Front service brake"),
          point("فرامل الخدمة الخلفية", "Rear service brake"),
          point("مشوار دواسة الفرامل", "Brake-pedal travel"),
          point("أنابيب الفرامل", "Brake pipes"),
          point("خراطيم الفرامل", "Brake hoses"),
          point("فرامل التوقف", "Parking brake"),
          point("نظام منع انغلاق المكابح", "Anti-lock brakes"),
          point("تيل الفرامل الأمامي", "Front brake pads"),
          point("تيل الفرامل الخلفي", "Rear brake pads"),
          point("الفك الأمامي", "Front caliper"),
          point("الفك الخلفي", "Rear caliper"),
          point("القرص الأمامي", "Front disc"),
          point("القرص الخلفي", "Rear disc"),
          point("كابل فرامل اليد", "Handbrake cable"),
          point("الكبح أثناء تجربة الطريق", "Braking on the test drive"),
          point("الكبح التلقائي إن وُجد", "Automatic braking if fitted"),
          point("التثبيت التلقائي إن وُجد", "Auto hold if fitted"),
          point("مستوى سائل الفرامل", "Brake-fluid level"),
          point("تآكل ظاهر على الأقراص", "Visible disc wear"),
        ],
      },
    ],
  },
  {
    id: "interior",
    title: { ar: "المقصورة", en: "Interior" },
    intro: "نقيّم المقصورة بعد تنظيفها: المقاعد، الرائحة، الأحزمة، وحيز التحميل. عدد المقاعد يُسجَّل كما هو.",
    groups: [
      {
        title: { ar: "حالة المقصورة", en: "Interior condition" },
        items: [
          point("المقصورة بعد التنظيف", "Cabin after cleaning"),
          point("تنجيد المقاعد", "Seat upholstery"),
          point("حيز التحميل", "Load space"),
          point("الرائحة", "Odour"),
          point("عدد المقاعد", "Seat count"),
          point("حزام الأمان الأمامي", "Front seatbelt"),
          point("حزام الأمان الخلفي", "Rear seatbelt"),
          point("لوحة القيادة", "Dashboard"),
          point("تآكل المقود", "Steering-wheel wear"),
          point("الدواسات", "Pedals"),
          point("بطانة السقف", "Headliner"),
          point("كسوة الأرضية", "Floor covering"),
          point("إضاءة صندوق الأمتعة", "Boot light"),
          point("المرآة الداخلية", "Interior mirror"),
          point("حاجب الشمس", "Sun visor"),
          point("مقبس 12 فولت", "12-volt socket"),
          point("صندوق القفازات", "Glovebox"),
          point("جيوب الأبواب", "Door pockets"),
          point("مساند الرأس", "Headrests"),
          point("حزام تثبيت الحمولة", "Load strap"),
        ],
      },
    ],
  },
  {
    id: "exterior",
    title: { ar: "الهيكل الخارجي", en: "Exterior" },
    intro: "نغسل السيارة قبل الحكم على الطلاء والصدأ والزجاج. التآكل المعتاد لعمر السيارة وعدادها لا يُكتب ملاحظة في الإعلان.",
    groups: [
      {
        title: { ar: "الهيكل والطلاء", en: "Body and paint" },
        items: [
          point("أعمال الهيكل", "Body repairs"),
          point("الشاسيه الظاهر", "Visible chassis"),
          point("باب الصندوق", "Boot lid"),
          point("الأبواب", "Doors"),
          point("غطاء المحرك", "Bonnet"),
          point("الحليات", "Trim"),
          point("القطع البلاستيكية", "Plastic parts"),
          point("الطلاء", "Paint"),
          point("الصدأ", "Rust"),
          point("الجنوط", "Wheels"),
          point("الزجاج الأمامي", "Windscreen"),
          point("الزجاج الخلفي", "Rear window"),
          point("زجاج الأبواب", "Door glass"),
          point("كسر ظاهر في المصابيح", "Visible lamp damage"),
          point("الصدام الأمامي", "Front bumper"),
          point("الصدام الخلفي", "Rear bumper"),
          point("الرفارف", "Wings"),
          point("عتبات الأبواب الظاهرة", "Visible door sills"),
          point("الهوائي", "Antenna"),
          point("الماسحة الخلفية إن وُجدت", "Rear wiper if fitted"),
        ],
      },
    ],
  },
];

export function vehicleTestPointCount(
  sections: readonly VehicleTestSection[] = VEHICLE_TEST_SECTIONS,
): number {
  return sections.reduce(
    (total, section) =>
      total + section.groups.reduce((sum, group) => sum + group.items.length, 0),
    0,
  );
}
